import { TitleCasePipe, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectorRef, Component, inject, Inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { Store } from '@ngrx/store';
import moment from 'moment-timezone';
import { combineLatest, Subject, switchMap } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

import { getProductCodename, getProductCodenameFromSubscription } from '@core/billing-plans-legacy';
import { DevFlagSelectors } from '@core/dev-flags-data';
import { DevFlagsMap } from '@core/dev-flags-types';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import type { BillingPlanTier } from '@core/products-types';
import type { PricingFeatureSubscription } from '@core/subscription-types';
import {
  ButtonComponent,
  CheckboxComponent,
  IconComponent,
  DividerComponent,
  HeadingComponent,
  BoxComponent,
} from '@core/user-interface-legacy';

import { readyOnce } from '../../../helpers/rx-operators';
import {
  PricingFeatureFacade,
  PricingFeatureObservableModel,
} from '../../../types/pricing-feature.types';
import { ProductOrderHeaderComponent } from '../product-order-header/product-order-header.component';
import { ProductOrderParamsIT, ProductOrderStepUrl } from '../product-order.types';
import { MultilinkDowngradeItemComponent } from './multilink-downgrade-item/multilink-downgrade-item.component';
import type {
  DowngradedFeatureItem,
  DowngradeViewTextsI,
  MissingFeature,
} from './product-order-downgrade.types';
import { DowngradeTextsToken } from './product-order-downgrade.types';

@Component({
  selector: 'rt-product-order-downgrade-step',
  templateUrl: './product-order-downgrade-step.component.html',
  styleUrls: ['./product-order-downgrade-step.component.less'],
  imports: [
    ProductOrderHeaderComponent,
    BoxComponent,
    HeadingComponent,
    MultilinkDowngradeItemComponent,
    DividerComponent,
    IconComponent,
    NgTemplateOutlet,
    CheckboxComponent,
    ButtonComponent,
    TitleCasePipe,
    TranslatePipe,
  ],
})
export class ProductOrderDowngradeStepComponent {
  public formattedDowngradeDate: string;
  public currentProductCode: BillingPlanTier;
  public selectedProductCodename: BillingPlanTier;
  public selectedProductHumanReadableName: string;
  public trial: boolean;
  public expandedWhatHappens: boolean;

  // 2cols
  public downgradedFeatures: [DowngradedFeatureItem[], DowngradedFeatureItem[]] | [] = [[], []];
  public missingFeatures: MissingFeature[] = [];
  public header: string;
  public confirmed: boolean;

  private params$ = inject(ProductOrderParamsIT);
  private ngOnDestroy$ = new Subject<void>();
  private titleCasePipe = new TitleCasePipe();

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private facade: PricingFeatureFacade,
    private model$: PricingFeatureObservableModel,
    private cdRef: ChangeDetectorRef,
    private i18n: I18nService,
    private store: Store,
    @Inject(DowngradeTextsToken) public texts: DowngradeViewTextsI,
  ) {}

  public getDowngradedFeaturesInfo(
    trial: boolean,
  ): [DowngradedFeatureItem[], DowngradedFeatureItem[]] {
    return [
      this.texts.downgradedFeaturesLeft.filter(item => (item.hideOnTrial ? !trial : true)),
      this.texts.downgradedFeaturesRight.filter(item => (item.hideOnTrial ? !trial : true)),
    ];
  }

  public toggleWhatHappens() {
    this.expandedWhatHappens = !this.expandedWhatHappens;

    this.cdRef.detectChanges();
  }

  public toggleConfirmation() {
    this.confirmed = !this.confirmed;
    this.cdRef.detectChanges();
  }

  public next() {
    this.router.navigate(['../', ProductOrderStepUrl.OrderBilling], {
      relativeTo: this.route,
      state: {
        downgradeScreenViewed: true,
      },
    });
  }

  public cancel() {
    const urlTree = this.router.parseUrl(this.router.url);
    delete urlTree.root.children.dialog;
    this.router.navigateByUrl(urlTree);
  }

  public upgrade() {
    this.facade.openPlanPicker();
  }

  public trackByIndex(index: number) {
    return index;
  }

  public ngOnInit() {
    return combineLatest([
      this.params$.pipe(
        switchMap(params => this.facade.getBillingPlanById(params.productId)),
        map(product => getProductCodename(product)),
      ),
      this.model$.pipe(
        readyOnce,
        map(model => ({
          trial: !!model.trial,
          currentProductCode: model.trial
            ? model.trial.productCode
            : getProductCodenameFromSubscription(model.subscription),
          isPP24: model.isPP24,
          subscription: model.subscription,
        })),
      ),
      this.store.select(DevFlagSelectors.data),
    ])
      .pipe(takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(this.onContextUpdate);
  }

  public ngOnDestroy() {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  private t(...args: [string, any?]) {
    return this.i18n.translate(...args);
  }

  private onContextUpdate = ([
    selectedProductCodename,
    { trial, currentProductCode, isPP24, subscription },
    flagsMap,
  ]: [
    BillingPlanTier,
    {
      trial: boolean;
      currentProductCode: BillingPlanTier;
      isPP24: boolean;
      subscription: PricingFeatureSubscription | undefined;
    },
    DevFlagsMap,
  ]) => {
    this.trial = trial;

    this.currentProductCode = currentProductCode;
    this.formattedDowngradeDate = this.getDowngradeDate({ subscription, isPP24 });
    this.selectedProductCodename = selectedProductCodename;
    this.selectedProductHumanReadableName = this.titleCasePipe.transform(selectedProductCodename);

    this.missingFeatures = [
      ...this.texts.features.filter(feature =>
        feature.devFlag ? flagsMap[feature.devFlag] : true,
      ),
    ];
    this.downgradedFeatures = this.getDowngradedFeaturesInfo(trial);

    this.header = trial
      ? this.t(this.texts.headerTrial, {
          planName: this.selectedProductHumanReadableName,
        })
      : this.t(this.texts.header, {
          planName: this.selectedProductHumanReadableName,
        });

    this.cdRef.detectChanges();
  };

  private getDowngradeDate(args: {
    subscription?: PricingFeatureSubscription;
    isPP24: boolean;
  }): string {
    if (!args.subscription?.contractTerm?.contractEnd || !args.isPP24)
      return moment().format('DD MMMM YYYY');
    return moment(args.subscription.contractTerm.contractEnd).add(1, 'day').format('DD MMMM YYYY');
  }
}
